import type { PostHog } from "posthog-js";

// ---------------------------------------------------------------------------
// The PostHog instance, once it exists
// ---------------------------------------------------------------------------
//
// posthog-js is 93 KB gzipped (288 KB raw) and used to ship in a chunk every
// page loaded before it could hydrate. gatsby-browser.js now imports it
// dynamically after the `load` event and hands the instance over here, so
// nothing else in the app imports posthog-js directly.
//
// Code that runs earlier (the landing $pageview, UTM and referral super
// properties, an early click) goes through withPostHog(), which queues the
// call and replays it once PostHog is up. Queued captures keep the time they
// happened, see captureWhenReady().

let instance: PostHog | undefined;
const waiting: Array<(ph: PostHog) => void> = [];

/** The PostHog instance if it has loaded, else undefined. Never queues. */
export function loadedPostHog(): PostHog | undefined {
  return instance && instance.__loaded ? instance : undefined;
}

/** Run fn with PostHog now if it has loaded, or as soon as it does. */
export function withPostHog(fn: (ph: PostHog) => void): void {
  const ph = loadedPostHog();
  if (ph) fn(ph);
  else waiting.push(fn);
}

/**
 * Capture an event, now or once PostHog loads, stamped with the time of the
 * call so a queued landing $pageview doesn't land after the clicks that
 * followed it.
 */
export function captureWhenReady(
  event: string,
  properties: Record<string, unknown>,
): void {
  const timestamp = new Date();
  withPostHog((ph) => ph.capture(event, properties, { timestamp }));
}

/** Called by gatsby-browser.js after posthog.init(); flushes the queue. */
export function setPostHog(ph: PostHog): void {
  instance = ph;
  if (!ph.__loaded) return;
  for (const fn of waiting.splice(0)) {
    try {
      fn(ph);
    } catch {
      // One bad queued call must not drop the rest.
    }
  }
}
